import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ApsPermitedScreen } from '../../models/aps-permission/aps-permited-screen';
import { DealerCountryDto } from '../../models/dealer/dealer-country-dto';
import { SaleChannel } from '../../models/enum/sale-channel';
import { StatusChassi } from '../../models/enum/status-chassi';
import { VinValidate } from '../../models/vin-validate/vin-validate';
import { ApsPermissionService } from '../../services/aps-permission/aps-permission.service';
import { ChoosePlanService } from '../../services/choose-plan/choose-plan.service';
import { ClientDataService } from '../../services/client-data/client-data.service';
import { ContractSummaryService } from '../../services/contract-summary/contract-summary.service';
import { DealerService } from '../../services/dealer/dealer.service';
import { I18nBrokerService } from '../../services/i18n/i18n-broker.service';
import { MessageExceptionService } from '../../services/message-exception/message-exception.service';
import { SystemCountryService } from '../../services/system-country/system-country.service';
import { VehicleInformationService } from '../../services/vehicle-information/vehicle-information.service';
import { VinValidateService } from '../../services/vin-validate-service/vin-validate-service.service';

@Component({
	selector: 'app-sales-channel',
	templateUrl: './sales-channel.component.html',
	styleUrls: ['./sales-channel.component.scss'],
	standalone: false
})
export class SalesChannelComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);

	isChassi = true; // vai ficar true ate definir a regra de negocio sem chassi
	chassi = "";
	vinValidate: VinValidate | undefined;
	inputChassiStyleCss = "";
	isValidChassi = StatusChassi.valid;
	isValidationSuccess = false; // Track successful validation for visual feedback
	saleChannel = SaleChannel.DEALER;
	dealerCountry: DealerCountryDto | undefined;
	lang: string | undefined;
	errorMessageVin: string | undefined;
	blockedUi = false;

	constructor(private vinValidateService: VinValidateService,
		private vehicleInformationService: VehicleInformationService,
		private translateService: TranslateService,
		private messageService: MessageService,
		private router: Router,
		private apsPermissionService: ApsPermissionService,
		private choosePlanService: ChoosePlanService,
		private clientDataService: ClientDataService,
		private contractSummaryService: ContractSummaryService,
		private messageExceptionService: MessageExceptionService,
		private dealerService: DealerService,
		private systemCountryService: SystemCountryService,
		private i18nBroker: I18nBrokerService
	) {
		this.systemCountryService.setSystemCountry();
	}

	ngOnInit() {
		this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenSales);
		this.vehicleInformationService.getAllChassiValidationClean();
		this.choosePlanService.getChoosePlanBuyngClean();
		this.clientDataService.getClientDataClean();
		this.contractSummaryService.getContractSummaryClean();

		this.getDealerCountry();
	}

	buttonNext() {
		this.messageService.clear();
		if (this.vinValidate && this.vinValidate.validated === StatusChassi.valid) {
			this.router.navigate(['vehicle-information', this.saleChannel], { skipLocationChange: true, replaceUrl: false });
		} else {
			this.messageService.add({ severity: 'error', summary: this.translateService.instant('TRANSLATE.ERROR'), detail: this.translateService.instant('TRANSLATE.CHASSI.INVALID') });
		}
	}

	onKeyUp(event: KeyboardEvent): void {

		this.chassi = this.chassi.toUpperCase();
		if (this.chassi.length < 17) {
			this.inputChassiStyleCss = "";
			this.vinValidate = undefined;
			this.errorMessageVin = undefined;
			this.isValidationSuccess = false; // Reset visual indicator
		}
		if (event.key !== "Control" && this.chassi.length === 17) {
			this.buttonSearchChassi();
		}
	}

	buttonSearchChassi() {
		this.messageService.clear();
		this.vinValidateService.getAllVinValidateListClean();
		this.inputChassiStyleCss = "";

		if (this.chassi !== '') {
			this.isValidationSuccess = false; // Reset on new search
			this.searchChassiSaveAndVector(this.chassi);
		}
		else {
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.ENTER.CHASSIS.CORRECTLY')
			});
		}
	}

	searchChassiSaveAndVector(chassi: string) {
		this.messageService.clear();
		this.blockedUi = true;
		this.vinValidate = undefined;
		this.errorMessageVin = undefined;
		this.vinValidateService.getVinValidate(this.systemCountryService.getSystemCountry(), chassi, this.getDealerLanguage())
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((response: VinValidate) => {

				this.vinValidate = this.verifyChassiValidation(response);
				this.vinValidate.vin = chassi;
				this.vinValidate.idVinCategoryVersion = response.idVinCategoryVersion;
				this.errorMessageVin = this.vinValidate.errorMessage;

				if (this.vinValidate.validated === StatusChassi.valid
					&& this.errorMessageVin !== 'VIN.ERROR.SAVE_VECTOR') {

					this.messageService.add({
						severity: 'success',
						summary: this.translateService.instant('TRANSLATE.SUCCESS'),
						detail: this.translateService.instant('TRANSLATE.VEHICLE.FOUND')
					});
					this.vehicleInformationService.setAllChassiValidation(this.vinValidate);
					this.setInputChassiFeedbackStyle(true);
					this.isValidationSuccess = true; // Delegate focus+animation to app-cta-button

				}
				else {

					this.setInputChassiFeedbackStyle(false);
					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.VEHICLE.NOT.FOUND.TYPE.AGAIN')
					});
				}

				this.blockedUi = false;
			}, (err) => {

				this.vinValidate = undefined;
				this.setInputChassiFeedbackStyle(false);
				if (err.status === 400) {
					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.VEHICLE.NOT.FOUND.TYPE.AGAIN')
					});

				} else if (err.status === 401) {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
					});

				} else {

					this.messageService.add({
						severity: 'error',
						summary: this.translateService.instant('TRANSLATE.ERROR'),
						detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
					});
				}

				this.blockedUi = false;

			});
	}

	private setInputChassiFeedbackStyle(isValid: boolean): void {
		this.inputChassiStyleCss = isValid
			? 'border: 2px solid #0c8e1c;'
			: 'border: 2px solid #ec2626;';
	}

	verifyChassiValidation(vinValidate: VinValidate): VinValidate {

		if (vinValidate.errorMessage !== null && vinValidate.errorMessage.trim().toUpperCase() === 'TRANSLATE.ERROR.SAVE_VECTOR') {
			vinValidate.validated = StatusChassi.invalid;
		} else {
			vinValidate.validated = StatusChassi.valid;
		}

		return vinValidate;
	}

	get chassisPromptLabel(): string {
		const translatedLabel = this.translateService.instant('TRANSLATE.INSERT.THE.CHASSIS');

		if (this.i18nBroker.currentLanguage?.startsWith('es')) {
			return translatedLabel.replace(/chasis\s+del/i, 'chasis<br>del');
		}

		return translatedLabel;
	}

	/*
	 * Consulta no backend o país do dealer
	 */
	getDealerCountry() {
		this.dealerService.getDealerCountry()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (response) => {
					this.dealerCountry = response;
				},
				error: (error) => {

					if (error.status === 404) {
						// Esse erro será analizado e tratado futuramente.

					} if (error.status === 401) {

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.USER.WITHOUT.PERMISSION.REFRESH.PAGE')
						});

					} else {

						this.messageService.add({
							severity: 'error',
							summary: this.translateService.instant('TRANSLATE.ERROR'),
							detail: this.translateService.instant('TRANSLATE.ERROR.CONTACT.SYSTEM.ADMINISTRATOR')
						});
					}

				}
			});
	}

	/*
	 * Recupera o pais do usuario logado pelo token de acesso;
	 * Caso não encontre, recupera o país do SITECODE;
	 * Em ambos os casos, faz o parse do país do dealer com a tradução oficial (pt-BR, es-CL, es-CO etc)
	 */
	getDealerLanguage(): string {
		const systemCountry = this.systemCountryService.getSystemCountry();
		if (systemCountry !== null) {
			this.lang = this.i18nBroker.getDealerLanguageByCountry(systemCountry);
		} else if (this.dealerCountry) {
			this.lang = this.i18nBroker.getDealerLanguageByCountry(this.dealerCountry.countryAlternative);
		}
		return this.lang || '';
	}

}